import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
  Grid,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { MuiColorInput } from 'mui-color-input';
import api from '../services/api';

const Settings = () => {
  const [userEmail, setUserEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [brandColor, setBrandColor] = useState('#1976D2');
  const [logoUrl, setLogoUrl] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [useCustomBranding, setUseCustomBranding] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings');
        setUserEmail(response.data.email);
        setUseCustomBranding(response.data.use_custom_branding || false);
        setBrandColor(response.data.brand_color || '#1976D2');
        setLogoUrl(response.data.logo_url || '');
      } catch (error) {
        showSnackbar('Failed to load settings', 'error');
      }
    };
    fetchSettings();
  }, []);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    try {
      await api.put('/settings/password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      showSnackbar('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
    } catch (error) {
      showSnackbar(error.response?.data?.msg || 'Failed to update password', 'error');
    }
  };

  const handleBrandingUpdate = async () => {
    try {
      await api.put('/settings/branding', {
        use_custom_branding: useCustomBranding,
        brand_color: brandColor,
        logo_url: logoUrl,
      });
      showSnackbar('Branding settings updated successfully');
    } catch (error) {
      showSnackbar('Failed to update branding settings', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        mt: 4, 
        mb: 4,
        bgcolor: 'rgb(250, 250, 250)',
        '& .MuiContainer-root': {
          bgcolor: 'rgb(250, 250, 250)'
        }
      }}
    >
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Account Information
        </Typography>
        <TextField
          fullWidth
          label="Email"
          value={userEmail}
          disabled
          sx={{ mb: 3 }}
        />

        <Typography variant="h6" gutterBottom>
          Change Password
        </Typography>
        <Box component="form" onSubmit={handlePasswordChange}>
          <TextField
            fullWidth
            type={showCurrentPassword ? 'text' : 'password'}
            label="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            type={showNewPassword ? 'text' : 'password'}
            label="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            type="submit"
            disabled={!currentPassword || !newPassword}
          >
            Update Password
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Branding Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={useCustomBranding}
                  onChange={(e) => setUseCustomBranding(e.target.checked)}
                  color="primary"
                />
              }
              label="Use custom branding"
            />
          </Grid>
          {useCustomBranding && (
            <>
              <Grid item xs={12}>
                <MuiColorInput
                  label="Brand Color"
                  value={brandColor}
                  onChange={setBrandColor}
                  fullWidth
                  disabled={!useCustomBranding}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Logo URL"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  helperText="Enter the URL of your logo image"
                  disabled={!useCustomBranding}
                />
              </Grid>
              {logoUrl && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <img
                      src={logoUrl}
                      alt="Logo preview"
                      style={{ maxHeight: '100px' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        showSnackbar('Invalid logo URL', 'error');
                      }}
                    />
                  </Box>
                </Grid>
              )}
            </>
          )}
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleBrandingUpdate}
              disabled={useCustomBranding && !brandColor}
            >
              Save Branding Settings
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Settings; 